import { html, state } from 'lit-element'
import type { ApplicationsResponse, ContextMenu, ApplicationItem } from '../context-menu/models'
import { SVG_APP_SWITCHER_FILLED } from '@getgo/chameleon-icons'
import { authenticatedFetch } from '../../services/auth'
import { t } from '../../directives/translate'
import styles from './app-switcher.styles.scss'
import { environment } from '../../environments'
import type { I18NEvents, Locale } from '../../services/i18n'
import { getCurrentLocale, I18NNamespace } from '../../services/i18n'
import { getShellLogger } from '../../common/logger'
import { nothing } from 'lit-html'
import { getEventBus } from '../../services/namespaces/event-bus'
import { ShellElement } from '../../common/shell-element'
import { getApiBaseUrl } from '../../core/environment'

export class GoToAppSwitcher extends ShellElement {
  static readonly tagName = 'goto-app-switcher'

  @state()
  private appSwitcherMenuItems: readonly ContextMenu[] = []

  static get styles() {
    return styles
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('keydown', event => this.handleKeyDown(event))
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', event => this.handleKeyDown(event))
    super.disconnectedCallback()
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.code === 'Enter' || e.code === 'Space') {
      this.toggleIsOpenAttribute()
    }
  }

  private toggleIsOpenAttribute() {
    const menu = this.shadowRoot?.querySelector('.popover-menu')
    if (menu?.getAttribute('is-open') === null) {
      menu?.setAttribute('is-open', 'true')
    }
  }

  async firstUpdated() {
    const applications = await this.getApplications()
    this.setAppSwitcherMenuItems(applications)
    if (applications.length > 0) {
      this.subscribeToLocaleChange(applications)
    }
  }

  private subscribeToLocaleChange(applications: readonly ApplicationItem[]) {
    const { localeChanged } = getEventBus().subscribeTo<typeof I18NNamespace, typeof I18NEvents>(I18NNamespace)
    const onLocaleChange = () => {
      this.setAppSwitcherMenuItems(applications)
    }
    localeChanged.on(onLocaleChange)

    this.unsubscribeFunctions.push(() => {
      localeChanged.removeListener(onLocaleChange)
    })
  }

  private setAppSwitcherMenuItems(applications: readonly ApplicationItem[]) {
    this.appSwitcherMenuItems = this.getAppSwitcherMenuItems(applications)
  }

  private async getApplications() {
    let applications: readonly ApplicationItem[] = []

    try {
      // TODO Remove this 'if' in SCORE-1220
      // We need to bypass this auth because jiveApiBaseUrl in RC is not deployed yet.
      if (environment().environment === 'rc') {
        throw 'rc not set up'
      }

      const response = await authenticatedFetch<ApplicationsResponse>(
        `${getApiBaseUrl()}/applications/v1/applications`,
        {
          credentials: undefined,
        },
      )
      const applicationItems = (await response.json()).items
      applications = Array.isArray(applicationItems) ? applicationItems : []
    } catch (e) {
      getShellLogger().error('could not fetch Applications', e)
    }
    return applications
  }

  private getAppSwitcherMenuItems(applications: readonly ApplicationItem[]): readonly ContextMenu[] {
    return applications.map((applicationsItem: ApplicationItem) => ({
      text: this.getLocalizedAppName(applicationsItem),
      href: applicationsItem.url,
      isExternalLink: true,
      dataTest: applicationsItem.id.toLowerCase(),
    }))
  }

  private getLocalizedAppName(applicationsItem: ApplicationItem) {
    const selectedLocale = getCurrentLocale()
    const getLocalizedDetails = (locale: Locale) =>
      applicationsItem.localizedDetails.find(element => element.language.replace('-', '_') === locale)

    const localizedDetails = getLocalizedDetails(selectedLocale) ?? applicationsItem.localizedDetails[0]
    return localizedDetails.name
  }

  render() {
    return this.appSwitcherMenuItems.length > 0
      ? html` <chameleon-popover position="right-end" arrow="" class="popover-menu" menu z-index="70">
          <button class="menu-trigger" aria-label=${t('App Switcher')} data-test="app-switcher" slot="trigger">
            <chameleon-svg> ${SVG_APP_SWITCHER_FILLED}</chameleon-svg>
          </button>
          <goto-context-menu slot="content" .menuItems=${this.appSwitcherMenuItems}> </goto-context-menu>
        </chameleon-popover>`
      : nothing
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToAppSwitcher.tagName]: GoToAppSwitcher
  }
}
